<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Badge, Button, DescriptionDetails, DescriptionList, DescriptionTerm, InlineAlert, StateCard } from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import { useLibraryStore } from '../../stores/library'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { useControlSize } from '../../lib/library-layout'
import { readingProgressRatio } from '../../lib/reading-progress-summary'
import { errorMessage } from '../../lib/fs-access'
import { isBackedUp, isRegistered } from '../../lib/comic-sync'
import type { LibraryEntry } from '../../lib/library-entries'
import LibraryOverlay from './library-overlay.vue'

const props = defineProps<{ entry?: LibraryEntry }>()
const emit = defineEmits<{ close: []; organize: [entry: LibraryEntry] }>()
/** 顯示用的那一筆；本機那一份只在講索引、來源與閱讀進度時才需要。 */
const comic = computed(() => props.entry?.comic)
const localComic = computed(() => props.entry?.local)
const local = useLibraryStore()
const cloud = useCloudLibraryStore()
const controlSize = useControlSize()
const cloudCategoryIds = ref<string[]>([])
const loading = ref(false)
const error = ref('')
let generation = 0

const dateFormat = new Intl.DateTimeFormat('zh-Hant', { dateStyle: 'medium', timeStyle: 'short' })

const categoryNames = computed(() => {
  // 分類的真相在雲端目錄；這一本在雲端有目錄項時用取回來的那一份，否則用本機鏡像。
  if (comic.value?.cloudComicId) {
    const ids = new Set(cloudCategoryIds.value)
    return cloud.categories.filter(category => ids.has(category.id)).map(category => category.name)
  }
  const ids = new Set(local.assignments.filter(a => a.comicId === localComic.value?.id).map(a => a.categoryId))
  return local.categories.filter(category => ids.has(category.id)).map(category => category.name)
})
const indexState = computed(() => {
  const mine = localComic.value
  if (!mine) return '這台裝置沒有這本作品的檔案'
  if (local.indexing.has(mine.id)) return '正在建立頁面尺寸索引'
  if (local.indexErrors[mine.id]) return '索引待重試'
  return mine.indexed ? '已完成頁面尺寸索引' : '尚未建立頁面尺寸索引'
})
const cloudState = computed(() => {
  const item = comic.value
  if (!item) return ''
  if (isBackedUp(item)) return '已備份到私人雲端'
  return isRegistered(item) ? '已在雲端目錄，尚未備份' : '尚未加入雲端目錄'
})
const location = computed(() => {
  const mine = localComic.value
  if (!mine) return '私人雲端（D1 metadata · R2 圖片）'
  const source = local.sourceOf(mine)
  if (!source) return '找不到這本作品的來源'
  return source.kind === 'comic' ? `${source.name}（直接匯入的作品資料夾）` : `${source.name} ／ ${mine.relPath}`
})
const readingState = computed(() => {
  const mine = localComic.value
  if (!mine) return '（閱讀進度記在持有檔案的裝置上）'
  const stored = local.progress.get(mine.id)
  if (!stored) return '尚未開始閱讀'
  const percent = Math.round(readingProgressRatio(stored.page, mine.pageCount) * 100)
  return `第 ${stored.page + 1} 頁（${percent}%）· ${dateFormat.format(stored.updatedAt)}`
})
const indexError = computed(() => localComic.value ? local.indexErrors[localComic.value.id] : undefined)

// 雲端分類指派不在本地 store 內，只有真的開啟資訊時才取一次。
watch(() => comic.value?.cloudComicId, async id => {
  const token = ++generation
  cloudCategoryIds.value = []
  error.value = ''
  if (!id) return
  loading.value = true
  try {
    const detail = await cloud.detail(id)
    if (token === generation) cloudCategoryIds.value = detail.categoryIds
  } catch (cause) { if (token === generation) error.value = errorMessage(cause) }
  finally { if (token === generation) loading.value = false }
}, { immediate: true })
</script>

<template>
  <LibraryOverlay :open="!!comic" title="作品資訊" description="這一本在書庫裡的完整紀錄。修改請用整理作品。" @close="$emit('close')">
    <InlineAlert v-if="indexError" tone="warning" class="mb-4">{{ indexError }}</InlineAlert>
    <InlineAlert v-if="error" tone="error" class="mb-4">{{ error }}</InlineAlert>
    <DescriptionList class="comic-info-list">
      <DescriptionTerm>書名</DescriptionTerm>
      <DescriptionDetails>{{ comic?.title }}</DescriptionDetails>
      <DescriptionTerm>作者</DescriptionTerm>
      <DescriptionDetails>{{ comic?.author || '作者未詳' }}</DescriptionDetails>
      <DescriptionTerm>頁數</DescriptionTerm>
      <DescriptionDetails>{{ comic?.pageCount ?? 0 }} 頁</DescriptionDetails>
      <DescriptionTerm>封面</DescriptionTerm>
      <DescriptionDetails>第 {{ (comic?.coverPage ?? 0) + 1 }} 頁</DescriptionDetails>
      <DescriptionTerm>加入時間</DescriptionTerm>
      <DescriptionDetails>{{ comic ? dateFormat.format(comic.addedAt) : '' }}</DescriptionDetails>
      <DescriptionTerm>位置</DescriptionTerm>
      <DescriptionDetails class="break-words">{{ location }}</DescriptionDetails>
      <DescriptionTerm>索引狀態</DescriptionTerm>
      <DescriptionDetails>{{ indexState }}</DescriptionDetails>
      <DescriptionTerm>雲端狀態</DescriptionTerm>
      <DescriptionDetails>{{ cloudState }}</DescriptionDetails>
      <DescriptionTerm>閱讀進度</DescriptionTerm>
      <DescriptionDetails>{{ readingState }}</DescriptionDetails>
      <DescriptionTerm>分類</DescriptionTerm>
      <DescriptionDetails>
        <StateCard v-if="loading" title="正在讀取雲端分類" message="取得這一本在雲端的分類指派…" :actionable="false" />
        <div v-else-if="categoryNames.length" class="comic-info-badges">
          <Badge v-for="name in categoryNames" :key="name" color="primary">{{ name }}</Badge>
        </div>
        <span v-else>尚未指派分類</span>
      </DescriptionDetails>
    </DescriptionList>
    <template #actions>
      <Button outline :size="controlSize" @click="$emit('close')">關閉</Button>
      <Button color="primary" :size="controlSize" @click="entry && emit('organize', entry)">整理作品</Button>
    </template>
  </LibraryOverlay>
</template>
